import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, filter, Observable, shareReplay, take} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule, DecimalPipe} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-spec-belasting-box1',
    templateUrl: './spec-belasting-box1.component.html',
    styleUrls: ['./spec-belasting-box1.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    DecimalPipe,
    CommonModule,
    SharedUiButtonModule,
    SharedUiDialogModule
  ]
})
export class SpecBelastingBox1Component implements OnInit {

  @Input() public inkomenBox1!: number;
  @Input() public inkomenBox2!: number;
  @Input() public inkomenBox3!: number;
  @Input() public premieVV!: number;
  @Input() public belastingBox1!: number;
  @Input() public belastingBox2!: number;
  @Input() public belastingBox3!: number;
  protected readonly Math = Math;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(IbRekenService);
  private rapportRequestId = 0;
  public dialogVisible = false;
  public dataVisible$ = new BehaviorSubject<boolean>(false);
  public isDefAangifte = false;

  public formGroupSpecBox1 = new FormGroup({
    berekendeIB: new FormControl<number | null>(0),
    schijf1: new FormControl<number | null>(0),
    schijf2: new FormControl<number | null>(0),
    schijf3: new FormControl<number | null>(0),
    schijf4: new FormControl<number | null>(0),
    totaalSchijf1EnSchijf2: new FormControl<number | null>(0),
    tariefsaanpassingAftrekEigenWoning: new FormControl<number | null>(0),
    voorkomingVerrekenmethode: new FormControl<number | null>(0),
    voorkomingVrijstellingsmethode: new FormControl<number | null>(0),
    lengteSchijf2: new FormControl<number | null>(0),
    lengteSchijf3: new FormControl<number | null>(0),
  });

  public formGroupSpecBox2 = new FormGroup({
    box2VoorVoorkoming: new FormControl<number | null>(0),
    voorkomingBox2: new FormControl<number | null>(0),
    berekendeIB: new FormControl<number | null>(0),
    box2EersteSchijf: new FormControl<number | null>(0),
    box2TweedeSchijf: new FormControl<number | null>(0),
    lengteSchijf2: new FormControl<number | null>(0),
  });

  public formGroupSpecBox3 = new FormGroup({
    berekendeIB: new FormControl<number | null>(0),
    box3VoorVoorkoming: new FormControl<number | null>(0),
    verrekeningBuitenlandseBronheffing: new FormControl<number | null>(0),
    vrijstellingBuitenlandsVermogen: new FormControl<number | null>(0),
    rendementBanktegoeden: new FormControl<number | null>(0),
    rendementBeleggingen: new FormControl<number | null>(0),
    rendementSchulden: new FormControl<number | null>(0),
    tarief: new FormControl<number | null>(0),
  });

  public formGroupSpecPremies = new FormGroup({
    premieVV: new FormControl<number | null>(0),
    premieInkomenAOWenANW: new FormControl<number | null>(0),
    premieInkomenWLZenAZVW: new FormControl<number | null>(0),
    premieAOW: new FormControl<number | null>(0),
    premieANW: new FormControl<number | null>(0),
    premieWLZ: new FormControl<number | null>(0),
    aowPercentage: new FormControl<number | null>(0),
    anwPercentage: new FormControl<number | null>(0),
    wlzPercentage: new FormControl<number | null>(0),
    gecombineerdHeffingspercentage: new FormControl<number | null>(0),
  });

  public formGroupWetgeving = new FormGroup({
    box1PercentageSchijf1: new FormControl<number | null>(0),
    box1PercentageSchijf2: new FormControl<number | null>(0),
    box1PercentageSchijf3: new FormControl<number | null>(0),
    tariefsaanpassingAftrekEigenWoning: new FormControl<number | null>(0),
    maxBox1Inkomen1eSchijf: new FormControl<number | null>(0),
    maxBox1Inkomen2eSchijf: new FormControl<number | null>(0),
    box2PercentageSchijf1: new FormControl<number | null>(0),
    box2PercentageSchijf2: new FormControl<number | null>(0),
    maxBox2Inkomen1eSchijf: new FormControl<number | null>(0),
    rendementBanktegoeden: new FormControl<number | null>(0),
    rendementBeleggingen: new FormControl<number | null>(0),
    rendementSchulden: new FormControl<number | null>(0),
    tariefBox3: new FormControl<number | null>(0),
    vermogensGrensVoorRegelingen: new FormControl<number | null>(0),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.isDefAangifte = data.taxType === 'DA';
    })
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isPartner) => {
      const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
      if (!aangifte) {
        this.dataVisible$.next(false);
        return;
      }
      this.getRapportIfChanged(aangifte, isPartner);
    });
  }

  public getRapportIfChanged(aangifte: Aangifte, partner : boolean) {
    const requestId = ++this.rapportRequestId;
    this.dataVisible$.next(false);

    if (partner) {
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      })
    }
    else {
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      });
    }
  }

  private resetAndPatchForms(details: AangifteRapport): void {
    this.formGroupWetgeving.reset({}, {emitEvent: false});
    this.formGroupWetgeving.patchValue(details.specificatieWetgeving|| {}, {emitEvent: false});
    this.formGroupWetgeving.disable({emitEvent: false});

    this.formGroupSpecBox1.reset({}, {emitEvent: false});
    this.formGroupSpecBox1.patchValue(details.specificatieBelastingBox1?.opbouw || {}, {emitEvent: false});
    this.formGroupSpecBox1.patchValue({totaalSchijf1EnSchijf2: (details.specificatieBelastingBox1?.opbouw?.schijf1 ?? 0) + (details.specificatieBelastingBox1?.opbouw?.schijf2 ?? 0) }, {emitEvent: false});
    this.formGroupSpecBox1.patchValue({berekendeIB: this.belastingBox1}, {emitEvent: false});
    this.formGroupSpecBox1.patchValue({lengteSchijf2: this.lengteSchijf2}, {emitEvent: false});
    this.formGroupSpecBox1.patchValue({lengteSchijf3: this.lengteSchijf3}, {emitEvent: false});
    this.formGroupSpecBox1.disable({emitEvent: false});

    this.formGroupSpecBox2.reset({}, {emitEvent: false});
    this.formGroupSpecBox2.patchValue(details.specificatieBelastingBox2?.opbouw || {}, {emitEvent: false});
    this.formGroupSpecBox2.patchValue({berekendeIB: this.belastingBox2}, {emitEvent: false});
    this.formGroupSpecBox2.patchValue({lengteSchijf2: this.lengteSchijf2Box2}, {emitEvent: false});
    this.formGroupSpecBox2.disable({emitEvent: false});

    this.formGroupSpecBox3.reset({}, {emitEvent: false});
    this.formGroupSpecBox3.patchValue(details.specificatieBelastingBox3?.opbouw || {}, {emitEvent: false});
    this.formGroupSpecBox3.patchValue({berekendeIB: this.belastingBox3}, {emitEvent: false});
    this.formGroupSpecBox3.disable({emitEvent: false});

    this.formGroupSpecPremies.reset({}, {emitEvent: false});
    this.formGroupSpecPremies.patchValue(details.specificatiePVV?.opbouw || {}, {emitEvent: false});
    this.formGroupSpecPremies.patchValue({premieVV: this.premieVV}, {emitEvent: false});
    this.formGroupSpecPremies.disable({emitEvent: false});

    this.dataVisible$.next(true);
  }

  showDialog(): void {
    this.dialogVisible = true;
  }

  get lengteSchijf2(): any {
    const maxSchijf1 = this.formGroupWetgeving.get('maxBox1Inkomen1eSchijf')?.value ?? 0;
    const maxSchijf2 = this.formGroupWetgeving.get('maxBox1Inkomen2eSchijf')?.value ?? 0;
    if (this.inkomenBox1 > maxSchijf2) {
      return maxSchijf2 - maxSchijf1;
    }
    else {
      return this.inkomenBox1 - maxSchijf1;
    }
  }

  get lengteSchijf3(): any {
    const maxSchijf2 = this.formGroupWetgeving.get('maxBox1Inkomen2eSchijf')?.value ?? 0;
    return this.inkomenBox1 - maxSchijf2;
  }

  get lengteSchijf2Box2(): any {
    const maxSchijf1 = this.formGroupWetgeving.get('maxBox2Inkomen1eSchijf')?.value ?? 0;
    return this.inkomenBox2 - maxSchijf1;
  }

}
